'use client';

import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { fetchWithCsrf } from '@/lib/csrf';
import { apiErrorMessage } from '@/lib/api-error';
import { getAttribution, hasFullConsent, resolveAttribution } from '@/lib/attribution';

/**
 * Email capture for the lead magnets — and the ONLY conversion event on this
 * site that an anonymous visitor can reach.
 *
 * WHY THIS COMPONENT EXISTS
 * Both magnets used to sit behind a sign-in gate, so `trackLeadCapture()` could
 * only fire for someone who had already created an account. A paid click would
 * have had to sign up, wait for a magic-link email, come back and download
 * before Google Ads saw anything. It never happened once: the account's
 * conversion actions read "Conversion has never received data" for their entire
 * lifetime, and Smart Bidding therefore had no signal to bid on.
 * `/api/lead-capture` — a complete endpoint that stores the lead, syncs Brevo
 * and emails the assets — had no caller at all.
 *
 * WHAT THIS FORM DOES, AND WHAT IT DELIBERATELY DOES NOT
 * It sends one email and stops. It does not store a lead, hand over a file,
 * grant an identity, or report a conversion — /api/lead/verify does all of
 * that, once somebody proves they can read the address they typed.
 *
 * That ordering is the point, and an earlier version of this change got it
 * backwards: it handed the files over instantly and merely withheld the
 * MAILING until confirmation. Which closes the bounce problem and nothing else
 * — the junk row is still written, the asset is still gone, and a fake address
 * is still told apart from a real one only after it stopped mattering. The
 * magic-link gate this replaced verified first. So does this.
 *
 * It is still not a sign-in wall. Nothing here creates an account, asks for a
 * password, or leaves the visitor with a profile to manage: the round trip
 * through the inbox is kept because that is the part that proves something, and
 * the account is dropped because it never did.
 */

const P = 'pageContent.leadCapture.form';

interface LeadCaptureFormProps {
  /** Magnet key understood by /api/lead-capture (`free-vc-list`, `pitch-deck-templates`). */
  source: string;
  /** Button label, e.g. "Send me the list". */
  cta: string;
  /** One line under the heading explaining what arrives. */
  blurb?: string;
  /** Called once the confirmation email is on its way, so a page can react. */
  onCaptured?: () => void;
  /**
   * One line describing what the confirmation click unlocks, shown in the
   * success panel. /match-preview sets this because the thing waiting behind
   * the click is the uploader rather than a file.
   */
  unlockNote?: string;
}

/**
 * utm_* and gclid travel with the lead so paid traffic stays attributable.
 *
 * What is sent is decided by resolveAttribution() (lib/attribution): ONE
 * visit's set, never a mix. The current URL's set wins whole if it carries any
 * utm_* or gclid; otherwise the stored first-touch set (written by PageViewTracker on
 * whatever page the visitor landed on) is used whole. That fallback matters
 * because a visitor who clicked an ad and then moved around the site reaches
 * this form with the query string already gone.
 *
 * The gclid is an advertising identifier, so it is sent only once the visitor
 * accepted the cookie banner ('cookie-consent' === 'full'); the site defaults
 * to denied. utm_* name a campaign, not a person, and are sent regardless.
 */
function currentAttribution(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  return resolveAttribution({
    search: window.location.search,
    stored: getAttribution(),
    consentFull: hasFullConsent(),
  });
}

export function LeadCaptureForm({
  source,
  cta,
  blurb,
  onCaptured,
  unlockNote,
}: LeadCaptureFormProps) {
  const { t } = useTranslation();
  // English is the fallback for every key, so a missing translation renders English.
  const s = (key: string, fallback: string): string => t(`${P}.${key}`, { defaultValue: fallback });
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [company, setCompany] = useState(''); // honeypot — real users never see it
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (state === 'sending') return;
    setState('sending');
    setError(null);

    try {
      // fetchWithCsrf, not bare fetch: proxy.ts rejects every unexempted POST
      // without an x-csrf-token header matching the csrf-token cookie, and
      // /api/lead-capture is deliberately NOT exempt — it is a public write
      // endpoint, so it should keep the protection rather than opt out of it.
      // Submitting with plain fetch returned "Invalid CSRF token" 403s.
      const res = await fetchWithCsrf('/api/lead-capture', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          firstName: firstName || null,
          company, // honeypot
          source,
          ...currentAttribution(),
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(apiErrorMessage(t, data, s('errGeneric', 'Something went wrong.')));
        setState('error');
        return;
      }

      setState('done');
      onCaptured?.();
    } catch {
      setError(s('errNetwork', 'Network error. Please try again.'));
      setState('error');
    }
  }

  if (state === 'done') {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
        <p className="font-semibold text-gray-900 mb-1">{s('doneTitle', 'Check your inbox.')}</p>
        <p className="text-sm text-gray-600">
          <Trans
            i18nKey={`${P}.doneSent`}
            defaults="We sent a confirmation link to <b>{{email}}</b>."
            values={{ email }}
            components={{ b: <span className="font-medium" /> }}
          />{' '}
          {unlockNote ?? s('doneDefault', 'Open it and your download appears right here.')}
        </p>
        <p className="mt-3 text-xs text-gray-500">
          {s(
            'doneSpam',
            'No link? Check spam, or submit the form again to resend it. The link works for seven days.',
          )}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="rounded-xl border border-gray-200 bg-gray-50 p-6">
      {blurb && <p className="text-sm text-gray-600 mb-4">{blurb}</p>}

      <div className="grid gap-3 sm:grid-cols-2">
        <input
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder={s('firstName', 'First name (optional)')}
          autoComplete="given-name"
          className="w-full rounded-lg border border-gray-300 px-4 py-3"
        />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          autoComplete="email"
          className="w-full rounded-lg border border-gray-300 px-4 py-3"
        />
      </div>

      {/* Honeypot. Hidden from humans and from assistive tech; bots fill it. */}
      <div className="hidden" aria-hidden="true">
        <label>
          Company
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
        </label>
      </div>

      <button
        type="submit"
        disabled={state === 'sending'}
        className="mt-4 w-full sm:w-auto bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
      >
        {state === 'sending' ? s('sending', 'Sending…') : cta}
      </button>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      <p className="mt-3 text-xs text-gray-500">
        {s(
          'footer',
          "We'll email you a confirmation link, then the download and occasional fundraising tips. Unsubscribe any time.",
        )}
      </p>
    </form>
  );
}
